import { useState } from 'react';
import { X, Plus, ExternalLink, BookOpen, Pencil, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/**
 * ReferencesManager - Sidebar component for managing article references/citations
 * 
 * Features:
 * - Add new references with text and optional URL
 * - Edit existing references
 * - Reorder references (move up/down)
 * - Delete references
 * - Shows reference number for each item
 */
export function ReferencesManager({
  citations = [],
  onCitationsChange,
  maxHeight = '300px'
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newUrl, setNewUrl] = useState('');
  
  // Edit mode state
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Parse citation HTML to extract text and URL
  const parseCitation = (citationHtml) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = citationHtml;
    const link = tmp.querySelector('a');
    
    if (link) {
      return {
        text: link.textContent || '',
        url: link.getAttribute('href') || ''
      };
    }
    
    return {
      text: tmp.textContent || tmp.innerText || '',
      url: ''
    };
  };

  // Create citation HTML from text and URL
  const createCitationHtml = (text, url) => {
    if (url && url.trim()) {
      return `<a href="${url.trim()}" target="_blank" rel="noopener noreferrer">${text.trim()}</a>`;
    }
    return text.trim();
  };

  const addCitation = () => {
    if (newText.trim()) {
      const citationHtml = createCitationHtml(newText, newUrl);
      onCitationsChange([...citations, citationHtml]);
      setNewText('');
      setNewUrl('');
      setIsAdding(false);
    }
  };

  const removeCitation = (index) => {
    onCitationsChange(citations.filter((_, i) => i !== index));
  };

  const openEditDialog = (index) => {
    const { text, url } = parseCitation(citations[index]);
    setEditingIndex(index);
    setEditText(text);
    setEditUrl(url);
    setEditDialogOpen(true);
  };

  const saveEdit = () => {
    if (editingIndex !== null && editText.trim()) {
      const updatedCitations = [...citations];
      updatedCitations[editingIndex] = createCitationHtml(editText, editUrl);
      onCitationsChange(updatedCitations);
      closeEditDialog();
    }
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditText('');
    setEditUrl('');
  };

  const moveCitation = (fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= citations.length) return;
    
    const newCitations = [...citations];
    const [removed] = newCitations.splice(fromIndex, 1);
    newCitations.splice(toIndex, 0, removed);
    onCitationsChange(newCitations);
  };

  // Strip HTML tags for preview
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Check if citation has a URL
  const hasUrl = (citationHtml) => {
    return citationHtml.includes('<a href=');
  };

  return (
    <div className="references-manager" data-testid="references-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">References</Label>
          {citations.length > 0 && (
            <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
              {citations.length}
            </span>
          )}
        </div>
      </div>

      {/* Citations List */}
      {citations.length > 0 && (
        <div 
          className="space-y-2 mb-3 overflow-y-auto pr-1" 
          style={{ maxHeight }}
        >
          {citations.map((citation, idx) => (
            <div 
              key={idx} 
              className="group flex items-start gap-2 p-2 bg-muted/30 rounded border border-border text-sm hover:bg-muted/50 transition-colors"
            >
              {/* Number badge */}
              <span className="shrink-0 w-5 h-5 bg-accent/10 text-accent rounded text-xs font-semibold flex items-center justify-center">
                {idx + 1}
              </span>
              
              {/* Citation text */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground line-clamp-2" title={stripHtml(citation)}>
                  {stripHtml(citation)}
                </p>
                {hasUrl(citation) && (
                  <span className="text-[10px] text-blue-500 flex items-center gap-0.5 mt-0.5">
                    <ExternalLink className="h-2.5 w-2.5" />
                    Has link
                  </span>
                )}
              </div>
              
              {/* Actions */}
              <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Edit button */}
                <button
                  type="button"
                  onClick={() => openEditDialog(idx)}
                  className="p-1 hover:bg-accent/10 rounded text-muted-foreground hover:text-accent"
                  title="Edit reference"
                  data-testid={`edit-ref-${idx}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => moveCitation(idx, 'up')}
                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                    title="Move up"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                )}
                {idx < citations.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveCitation(idx, 'down')}
                    className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                    title="Move down"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeCitation(idx)}
                  className="p-1 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Reference */}
      {isAdding ? (
        <div className="space-y-2 p-3 bg-muted/20 rounded border border-border">
          <Textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Enter reference text (e.g., Book title, Author, Year, Page)"
            className="text-sm min-h-[60px] resize-none"
            autoFocus
            data-testid="ref-manager-new-text"
          />
          <div className="flex items-center gap-1">
            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
            <Input
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="URL (optional)"
              className="text-sm h-8"
              data-testid="ref-manager-new-url"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              type="button" 
              size="sm" 
              className="h-7 text-xs"
              onClick={addCitation}
              disabled={!newText.trim()}
              data-testid="save-new-ref-btn"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={() => {
                setIsAdding(false);
                setNewText('');
                setNewUrl('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full h-8 text-xs"
          onClick={() => setIsAdding(true)}
          data-testid="add-reference-btn"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Reference
        </Button>
      )}

      {/* Helper text */}
      <p className="text-[10px] text-muted-foreground mt-2">
        Click the pencil icon to edit. Use "Insert Citation" in the editor to add inline markers.
      </p>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => !open && closeEditDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit Reference #{editingIndex !== null ? editingIndex + 1 : ''}
            </DialogTitle>
            <DialogDescription>
              Update the reference text and optional URL link.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-text">Reference Text</Label>
              <Textarea
                id="edit-text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Enter reference text..."
                className="min-h-[100px] resize-none"
                data-testid="edit-ref-text"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-url" className="flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                URL Link (Optional)
              </Label>
              <Input
                id="edit-url"
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                placeholder="https://example.com/source"
                data-testid="edit-ref-url"
              />
              <p className="text-xs text-muted-foreground">
                If provided, the reference will be clickable.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={!editText.trim()} data-testid="save-edit-ref-btn">
              <Check className="h-4 w-4 mr-1" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ReferencesManager;
